/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { lazy } from "react";

/** Views */
const Dashboard = lazy(() => import('features/dashboard/Dashboard'));
const Login = lazy(() => import("features/authentication/login"));
const Signup = lazy(() => import("features/authentication/signup"));
const Confirmation = lazy(() => import ("features/authentication/components"));
const ResetPassword = lazy(() => import("features/authentication/reset-psw"));
const UsersView = lazy(() => import("features/admin/users-table"));
const UploadConfigurations = lazy(() => import("features/admin/configurations/components/UploadConfigurations/UploadConfigurations"));
const Configurations = lazy(() => import("features/admin/configurations/Configurations"));
const Monitor = lazy(() => import("features/monitor/Monitor"));
const EditProfile = lazy(() => import("features/edit-profile/EditProfile"));
const DevicesRoot = lazy(() => import("features/devices/DevicesRoot"));
const LicencesRoot = lazy(() => import("features/licences/LicencesRoot"));
const BuyLicence = lazy(() => import("features/licences/buy-licence/BuyLicence"));
const Ota = lazy(() => import("features/admin/ota/Ota"));
const CreateSession = lazy(() => import("features/admin/ota/components/CreateSession"));
const Support = lazy(() => import("features/support/Support"));

/** 
  All of the routes for the Material Dashboard 2 PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

const routes = [
  {
    type: "collapse",
    name: "dashboard",
    key: "home",
    route: "/home",
    component: <Dashboard />,
    icon: "dashboard",
    authRequired: true,
    noCollapse: true,
  },
  {
    name: "not Authorized",
    key: "not-authorized",
    route: "/not-authorized",
    component: <p>Not Authorized</p>,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "devices",
    key: "devices",
    icon: "devices_others",
    route: "/devices",
    component: <DevicesRoot />,
    authRequired: true,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "licences",
    key: "licences",
    icon: "key",
    route: "/licences",
    component: <LicencesRoot />,
    authRequired: true,
    noCollapse: true,
    level: [0, 2]
  },
  {
    name: "licence-buy",
    key: "licence-buy",
    icon: "key",
    route: "/licences/buy",
    component: <BuyLicence />,
    authRequired: true,
    noCollapse: true,
    level: [2]
  },
  {
    name: "monitor",
    key: "monitor",
    route: "/devices/:deviceId",
    component: <Monitor />,
    authRequired: true,
    noCollapse: true,
  },
  {
    name: "custom-page",
    key: "custom-page",
    route: "/devices/:deviceId/:page",
    component: <Monitor />,
    authRequired: true,
    noCollapse: true,
  },
  {
    name: "Login",
    key: "login",
    route: "/login",
    component: <Login />,
    noCollapse: true,
  },
  {
    name: "SignUp",
    key: "signup",
    route: "/signup",
    component: <Signup />,
    noCollapse: true,
  },
  {
    name: "Confirmation",
    key: "confirmation",
    route: "/authentication/confirmation",
    component: <Confirmation />,
    noCollapse: true,
  },
  {
    name: "ResetPassword",
    key: "resetPassword",
    route: "/authentication/reset-password",
    component: <ResetPassword />,
    noCollapse: true,
  },
  { type: "divider", key: "divider-0", level: [0] },
  { type: "title", title: "Admin", key: "admin-pages", level: [0] },
  {
    type: "collapse",
    name: "users",
    key: "users",
    icon: "people_alt",
    route: "/users",
    component: <UsersView />,
    noCollapse: true,
    authRequired: true,
    level: [0]
  },
  {
    type: "collapse",
    name: "configurations",
    key: "configurations",
    icon: "settings",
    route: "/configurations",
    component: <Configurations />,
    noCollapse: true,
    authRequired: true,
    level: [0]
  },
  {
    name: "Add Configuration",
    key: "add-configs",
    route: "/configurations/add",
    component: <UploadConfigurations />,
    noCollapse: true,
    authRequired: true,
    level: [0]
  },
  {
    type: "collapse",
    name: "ota",
    key: "ota",
    icon: "memory",
    route: "/ota",
    component: <Ota />,
    noCollapse: true,
    authRequired: true,
    level: [0]
  },
  {
    name: "Create Session",
    key: "ota",
    route: "/ota/add",
    component: <CreateSession />,
    noCollapse: true,
    authRequired: true,
    level: [0]
  },
  {
    name: "Edit Profile",
    key: "edit-profile",
    route: "/edit-profile",
    component: <EditProfile />,
    noCollapse: true,
  },
  {
    name: "Support Delete Profile and Data",
    key: "support",
    route: "/support",
    component: <Support />,
    noCollapse: true,
  },
];

export default routes;
